import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../layout/builders/Layout";
import { ArrowLeft, User, Mail, Phone, MapPin, Briefcase, Award, Calendar, Star } from "lucide-react";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";

const ConsultantDetail = () => {
    const { consultantId } = useParams();
    const navigate = useNavigate();
    const { token } = useAuthStore();
    const [consultant, setConsultant] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchConsultantDetail();
    }, [consultantId]);

    const fetchConsultantDetail = async () => {
        try {
            setLoading(true);
            const response = await axiosInstancev1.get(`/consultants/profile/${consultantId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setConsultant(response.data?.consultant);
        } catch (error) {
            console.error("Error fetching consultant detail:", error);
            toast.error("Failed to load consultant details");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
            </Layout>
        );
    }

    if (!consultant) {
        return (
            <Layout>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Consultant Not Found</h3>
                    <p className="text-gray-500 text-sm mb-4">The consultant you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate("/builder/consultant-profiles")}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                    >
                        Back to List
                    </button>
                </div>
            </Layout>
        );
    }

    const displayName = consultant.engineer_name || consultant.engineerName || consultant.name || "Consultant";
    const displayImage = consultant.profile_image || consultant.profileImage || consultant.engineerPhoto || "/assets/Support.png";
    const hasActiveSubscription = consultant.subscription_status === 'active';

    return (
        <Layout>
            <div className="space-y-4 pb-4">
                {/* Back Button */}
                <button
                    onClick={() => navigate("/builder/consultant-profiles")}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <ArrowLeft size={18} />
                    <span className="text-sm font-medium">Back to Consultants</span>
                </button>

                {/* Profile Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="px-5 py-5">
                        <div className="flex items-start gap-5">
                            <div className="relative">
                                <img
                                    src={displayImage}
                                    alt={displayName}
                                    className="w-24 h-24 rounded-full border-2 border-gray-200 shadow-sm object-cover bg-gray-50"
                                    onError={(e) => { e.target.src = "/assets/Support.png"; }}
                                />
                                {hasActiveSubscription && (
                                    <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-1.5 border-2 border-white">
                                        <Star size={14} fill="white" className="text-white" />
                                    </div>
                                )}
                            </div>

                            <div className="flex-1">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
                                        <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                                            <MapPin size={14} />
                                            {consultant.district || "N/A"}, {consultant.taluk || "N/A"}
                                        </p>
                                    </div>
                                    {hasActiveSubscription && (
                                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                            Active Subscription
                                        </span>
                                    )}
                                </div>

                                <div className="mt-4 flex gap-4">
                                    <button
                                        onClick={() => navigate(`/builder/consultant-subscription/${consultantId}`)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors"
                                    >
                                        View Subscription
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Details Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Contact Information */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-sm font-bold text-gray-900 mb-4">Contact Information</h3>
                        <div className="space-y-3">
                            <InfoRow icon={<Mail size={16} />} label="Email" value={consultant.email || "N/A"} />
                            <InfoRow 
                                icon={<Phone size={16} />} 
                                label="Phone" 
                                value={consultant.phone_number || consultant.contactNumber || consultant.phone || "N/A"} 
                            />
                        </div>
                    </div>

                    {/* Professional Information */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-sm font-bold text-gray-900 mb-4">Professional Information</h3>
                        <div className="space-y-3">
                            <InfoRow 
                                icon={<Award size={16} />} 
                                label="Qualification" 
                                value={consultant.qualification || "N/A"} 
                            />
                            <InfoRow 
                                icon={<Briefcase size={16} />} 
                                label="Experience" 
                                value={consultant.experience ? `${consultant.experience} years` : "N/A"} 
                            />
                        </div>
                    </div>

                    {/* Location Details */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-sm font-bold text-gray-900 mb-4">Location Details</h3>
                        <div className="space-y-3">
                            <InfoRow 
                                icon={<MapPin size={16} />} 
                                label="District" 
                                value={consultant.district || "N/A"} 
                            />
                            <InfoRow 
                                icon={<MapPin size={16} />} 
                                label="Taluk" 
                                value={consultant.taluk || "N/A"} 
                            />
                            <InfoRow 
                                icon={<MapPin size={16} />} 
                                label="Village" 
                                value={consultant.village_name || consultant.villageName || "N/A"} 
                            />
                            <InfoRow 
                                icon={<MapPin size={16} />} 
                                label="Pincode" 
                                value={consultant.pincode || "N/A"} 
                            />
                        </div>
                    </div>

                    {/* Account Information */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <h3 className="text-sm font-bold text-gray-900 mb-4">Account Information</h3>
                        <div className="space-y-3">
                            <InfoRow 
                                icon={<User size={16} />} 
                                label="Profile Status" 
                                value={consultant.is_profile_active ? "Active" : "Inactive"} 
                                valueClass={consultant.is_profile_active ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}
                            />
                            {consultant.created_at && (
                                <InfoRow 
                                    icon={<Calendar size={16} />} 
                                    label="Joined" 
                                    value={new Date(consultant.created_at).toLocaleDateString('en-IN')} 
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

const InfoRow = ({ icon, label, value, valueClass = "text-gray-900" }) => (
    <div className="flex items-start gap-3">
        <div className="mt-0.5 text-gray-400">
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 mb-0.5">{label}</p>
            <p className={`text-sm font-medium ${valueClass} break-words`}>{value}</p>
        </div>
    </div>
);

export default ConsultantDetail;
